import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { wholesaleProducts } from '../../services/api';
import { useCart } from '../../contexts/CartContext';
import ProductCard from '../../components/customer/ProductCard';
import ProductDrawer from '../../components/customer/ProductDrawer';

const WINE_TYPES = ['Red', 'White', 'Rosé', 'Sparkling', 'Dessert'];

// Normalize a wine type string for comparison (strips accents, lowercases)
const normalizeType = (s) =>
  (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

// ── Pill button styles ────────────────────────────────────────────────────────
const pillBase = {
  borderRadius: 20,
  padding: '0.3rem 0.9rem',
  fontSize: '0.85rem',
  cursor: 'pointer',
  lineHeight: '1.4',
  fontFamily: 'inherit',
  transition: 'background 0.15s, color 0.15s',
};
const pillActive   = { ...pillBase, border: 'none',                               background: 'var(--sf-black)', color: 'var(--sf-cream)' };
const pillInactive = { ...pillBase, border: '1px solid var(--sf-border)', background: 'transparent' };

// ── Catalog ───────────────────────────────────────────────────────────────────
export default function Catalog() {
  const { addToCart, cartCount } = useCart();

  // ── Data ──────────────────────────────────────────────────────────────────
  const [products, setProducts] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    wholesaleProducts.list({})
      .then(res => setProducts(res.data.data || []))
      .catch(() => setError('Failed to load products. Please try again.'))
      .finally(() => setLoading(false));
  }, []);

  // ── Filters & sort ────────────────────────────────────────────────────────
  const [search,        setSearch]        = useState('');
  const [wineTypeFilter, setWineTypeFilter] = useState('');
  const [sortOrder,     setSortOrder]     = useState('name');

  const filtered = useMemo(() => {
    let result = [...products];

    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(p =>
        (p.proFanciful || '').toLowerCase().includes(q) ||
        (p.proVarietal || '').toLowerCase().includes(q) ||
        (p.proVintage  ? String(p.proVintage) : '').includes(q)
      );
    }

    if (wineTypeFilter) {
      const norm = normalizeType(wineTypeFilter);
      result = result.filter(p => normalizeType(p.proWineType) === norm);
    }

    if (sortOrder === 'price-asc') {
      result.sort((a, b) => (Number(a.displayPrice) || 0) - (Number(b.displayPrice) || 0));
    } else if (sortOrder === 'price-desc') {
      result.sort((a, b) => (Number(b.displayPrice) || 0) - (Number(a.displayPrice) || 0));
    } else {
      result.sort((a, b) => (a.proFanciful || '').localeCompare(b.proFanciful || ''));
    }

    return result;
  }, [products, search, wineTypeFilter, sortOrder]);

  // ── Drawer (6C) ───────────────────────────────────────────────────────────
  const [drawerProduct, setDrawerProduct] = useState(null);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const drawerRequestId = useRef(0);

  // Open the drawer with partial list data immediately, then fetch full data.
  // A request ID guards against stale async updates when the user quickly
  // switches between products.
  const openDrawer = async (product) => {
    const reqId = ++drawerRequestId.current;
    setDrawerProduct(product);
    setDrawerLoading(true);
    try {
      const res = await wholesaleProducts.get(product.productID);
      if (drawerRequestId.current === reqId) {
        setDrawerProduct(res.data.data);
      }
    } catch {
      // Keep the partial data — drawer is still useful
    } finally {
      if (drawerRequestId.current === reqId) {
        setDrawerLoading(false);
      }
    }
  };

  const closeDrawer = () => {
    drawerRequestId.current++;   // cancel any in-flight request
    setDrawerProduct(null);
    setDrawerLoading(false);
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="sf-page">

      {/* ── Sticky filter bar ── */}
      <div
        style={{
          background:   '#fff',
          borderBottom: '1px solid var(--sf-border)',
          padding:      '0.75rem 0',
          position:     'sticky',
          top:          56,
          zIndex:       50,
        }}
      >
        <div className="container-fluid px-4">
          <div className="d-flex flex-wrap align-items-center gap-2">

            {/* Search */}
            <input
              type="search"
              placeholder="Search wines…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{
                border:       '1px solid var(--sf-border)',
                borderRadius: 4,
                padding:      '0.4rem 0.75rem',
                fontSize:     '0.9rem',
                outline:      'none',
                minWidth:     180,
                fontFamily:   'inherit',
              }}
            />

            {/* Wine type pills */}
            <button
              style={wineTypeFilter === '' ? pillActive : pillInactive}
              onClick={() => setWineTypeFilter('')}
            >
              All
            </button>
            {WINE_TYPES.map(type => (
              <button
                key={type}
                style={normalizeType(wineTypeFilter) === normalizeType(type) ? pillActive : pillInactive}
                onClick={() => setWineTypeFilter(
                  normalizeType(wineTypeFilter) === normalizeType(type) ? '' : type
                )}
              >
                {type}
              </button>
            ))}

            {/* Sort */}
            <select
              value={sortOrder}
              onChange={e => setSortOrder(e.target.value)}
              style={{
                border:       '1px solid var(--sf-border)',
                borderRadius: 4,
                padding:      '0.4rem 0.75rem',
                fontSize:     '0.9rem',
                background:   '#fff',
                cursor:       'pointer',
                fontFamily:   'inherit',
              }}
            >
              <option value="name">Name A–Z</option>
              <option value="price-asc">Price Low–High</option>
              <option value="price-desc">Price High–Low</option>
            </select>

            {/* Results count */}
            {!loading && (
              <span className="text-muted small ms-auto">
                {filtered.length} wine{filtered.length !== 1 ? 's' : ''}
              </span>
            )}

          </div>
        </div>
      </div>

      {/* ── Main content ── */}
      <div className="container-fluid px-4 py-4">

        {/* Loading */}
        {loading && (
          <div className="d-flex justify-content-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading…</span>
            </div>
          </div>
        )}

        {/* Error */}
        {!loading && error && (
          <div className="alert alert-danger">{error}</div>
        )}

        {/* Product grid */}
        {!loading && !error && filtered.length > 0 && (
          <div className="row row-cols-2 row-cols-sm-3 row-cols-md-4 row-cols-xl-5 g-3">
            {filtered.map(product => (
              <div className="col" key={product.productID}>
                <ProductCard
                  product={product}
                  onDetail={() => openDrawer(product)}
                />
              </div>
            ))}
          </div>
        )}

        {/* Empty state */}
        {!loading && !error && filtered.length === 0 && products.length > 0 && (
          <div className="text-center py-5">
            <p className="sf-section-title mb-1">No wines match your search.</p>
            <p className="text-muted small">Try adjusting your filters.</p>
          </div>
        )}

        {/* No products at all */}
        {!loading && !error && products.length === 0 && (
          <div className="text-center py-5">
            <p className="sf-section-title mb-1">No products available yet.</p>
            <p className="text-muted small">Check back soon.</p>
          </div>
        )}

      </div>

      {/* ── Product detail drawer ── */}
      <ProductDrawer
        product={drawerProduct}
        loading={drawerLoading}
        onClose={closeDrawer}
        onAddToCart={addToCart}
      />

      {/* ── Mobile floating cart bar ── */}
      {cartCount > 0 && (
        <div
          className="d-lg-none position-fixed bottom-0 start-0 end-0 text-white p-3 d-flex justify-content-between align-items-center"
          style={{ background: 'var(--sf-dark)', zIndex: 1040 }}
        >
          <span className="small">{cartCount} item{cartCount !== 1 ? 's' : ''} in cart</span>
          <Link to="/wholesale/checkout" className="btn btn-sm" style={{ background: 'var(--sf-cream)', color: 'var(--sf-black)', fontWeight: 600 }}>
            Checkout &rarr;
          </Link>
        </div>
      )}

    </div>
  );
}
